<template>
    <div
        v-if="preFooter"
        :class="{
            'p-4': preFooter.preFooterLayout_Border === 'all-sides',
            'pb-1': preFooter.preFooterLayout_Border === 'bottom-only',
            'bg-(--shape-brand-accent)': preFooter.preFooterLayout_BorderColour === 'accent',
            'surface-primary': preFooter.preFooterLayout_BorderColour !== 'accent',
        }"
        :data-theme="colorTheme"
        :data-mode="preFooterBorderMode"
    >
        <div
            :data-theme="colorTheme"
            :data-mode="mode"
            class="surface-primary relative overflow-hidden"
            :class="{
                'rounded-card-lg': preFooter.preFooterLayout_Border === 'all-sides',
                'py-20': preFooter.preFooterLayout_ContainerPadding,
            }"
        >
            <div
                class="mx-auto"
                :class="containerStyles"
            >
                <div
                    class="flex w-full flex-col lg:grid lg:grid-cols-24"
                    :class="[
                        spaceBetweenClasses,
                        {
                            'relative z-20': preFooter.preFooterLayout_BackgroundElement?.length > 0,
                        },
                    ]"
                >
                    <BlocksAnimation
                        v-for="(item, index) in layoutBlocks"
                        :key="item.canonicalUid"
                        :animation-offset="index"
                        :style="{
                            'grid-column': `${item.grid.x + 1} / ${item.grid.x + item.grid.w + 1}`,
                            'grid-row': `${item.grid.y + 1} / ${item.grid.y + item.grid.h + 1}`,
                        }"
                    >
                        <BlocksLayoutWrapper
                            :block="item"
                            :parent-has-bg-element="preFooter.preFooterLayout_BackgroundElement?.length"
                            :class="`type-${item.__typename}-${item.typeIndex}`"
                            is-layout
                            :is-singleton="isSingleton(item)"
                        />
                    </BlocksAnimation>
                </div>
            </div>

            <BlocksBackgroundElement
                v-if="preFooter.preFooterLayout_BackgroundElement?.length > 0"
                :entry="preFooter.preFooterLayout_BackgroundElement[0]"
                class="z-10"
            />
            <slot name="prefooterDecoration" />
        </div>
    </div>
</template>

<script setup>
    const { entry } = storeToRefs(useEntryStore())

    const preFooter = computed(() => {
        return first(entry.value?.preFooter)
    })

    const colorTheme = computed(() => {
        return get(preFooter.value, 'preFooterLayout_ColorTheme')
    })

    const mode = computed(() => {
        switch (get(preFooter.value, 'preFooterLayout_ColorPalette')) {
            case 'color-subtle':
                return 'brand'
            case 'color-invert':
                return 'brand-invert'
            default:
                return get(preFooter.value, 'preFooterLayout_ColorPalette') || 'brand-invert'
        }
    })

    const preFooterBorderMode = computed(() => {
        switch (get(preFooter.value, 'preFooterLayout_BorderColour')) {
            case 'accent':
                return mode.value || 'base'
            case 'light':
                return 'brand'
            case 'dark':
                return 'brand-invert'
            case 'default':
            default:
                return 'base'
        }
    })

    const spaceBetweenClasses = computed(() => {
        switch (get(preFooter.value, 'preFooterLayout_ContainerSpacing')) {
            case 'none':
                return ''
            case 'sm':
                return 'gap-4'
            case 'md':
            default:
                return 'gap-6'
            case 'lg':
                return 'gap-8'
            case 'xl':
                return 'gap-10'
        }
    })

    const containerStyles = computed(() => {
        switch (get(preFooter.value, 'preFooterLayout_ContainerWidth')) {
            case 'full-width':
                return ''
            default:
                return 'container-extra-wide px-4 lg:px-6'
        }
    })

    const layoutBlocks = computed(() => {
        // map with grid data
        const gridLayout = get(preFooter.value, 'preFooterLayout_ContentBuilderLayout.grid') ?? []

        return map(get(preFooter.value, 'preFooterLayout_ContentBuilder'), (contentBlock, index) => {
            // index is a string in the response
            const layout = find(gridLayout, { index: index })

            return {
                ...contentBlock,
                grid: {
                    x: layout.x,
                    y: layout.y,
                    w: layout.width,
                    h: layout.height,
                },
            }
        })
    })

    // checks if this block is by itself on its own row
    const isSingleton = (block) => {
        return block.grid.x === 0 && block.grid.w === 24
    }
</script>
